import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { LoginService } from '../../Services/login.service';
import { GroupService } from '../../Services/group.service';
import { Location } from '@angular/common';
import swal from 'sweetalert2';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AdminControlConfirmDialogComponent } from './admin-control-confirm-dialog/admin-control-confirm-dialog.component';
import * as _ from 'lodash';
import { SharedService } from 'src/app/Services/sharedService';
import { ProjectService } from 'src/app/Services/project.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
	CreateUserDialogData,
	CreateUserDialogResult,
	CreatedEmployeePayload,
} from './create-user-dialog.types';
import { passwordValidator } from 'src/app/password-validators';
import { colors } from 'src/app/colors';
import { sendEmailVerification } from 'firebase/auth';
import { ModalService } from 'src/app/Services/modal.service';
import { DeviceDetectionService } from 'src/app/Services/device-detection.service';
import { PhoneVerificationService } from 'src/app/Services/phone-verification.service';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { EntitlementsService } from 'src/app/Services/entitlements.service';
import { EntitlementKey } from 'src/app/entitlements/entitlement-keys';
import {
	CreatableUserRoleOption,
	getCreatableUserRoleOptions,
	getRoleSelectHint,
	validateRoleAssignmentForEditor,
	canAssignLeadUserRole,
	canAssignManagerUserRole,
	UserRoleContext,
} from 'src/app/entitlements/plan-role.util';
import {
	getDefaultSoftwareVersionType,
	getSoftwareTypeOptions,
	resolveActivationHandoffVersionType,
	SoftwareTypeOption,
	SoftwareVersionType,
	validateSoftwareVersionType,
} from 'src/app/entitlements/plan-software-type.util';
import { PlanUpgradeDialogComponent } from 'src/app/components/plan-upgrade-dialog/plan-upgrade-dialog.component';
import { ActivationJourneyService } from 'src/app/onboarding-checklist/activation-journey.service';
import { Subscription, forkJoin, of } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';

@Component({
    selector: 'app-create-user',
    templateUrl: './create-user.component.html',
    styleUrls: ['./create-user.component.css'],
    standalone: false
})

export class CreateUserComponent implements OnInit, OnDestroy {
	/** Typed MAT_DIALOG_DATA — keep public dialogData for existing template/bindings. */
	adminId;
	isDisable = false;
	createUserForm: UntypedFormGroup;
	groupId: any = [];
	groups: any;
	loading = false;
	isEdit;
	gID;
	creatableRoleOptions: CreatableUserRoleOption[] = [];
	roleSelectHint = '';
	showRoleField = true;
	private loggedInUser: UserRoleContext | null = null;
	private entitlementsSub?: Subscription;
	selectedRole2 = '';
	/** Password field starts masked. */
	hide = true;
	hideConfirm: boolean = true;
	isEditData: any;
	isResponseGet: any = true;
	selectedValue = '';
	userEmail = '';
	emailPattern = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
	fakeEmailDomains = ['fakeemail.com', 'randommail.com', 'noemail.com', 'nowhere.com', 'doesnotexist.com', 'invalidemail.com', 'fakedomain.com', 'emailnotreal.com', 'emptyaddress.com'];
	isRandomEmail = false;
	generalFormValues: any;
	silentFormValues: any;
	restrictedFormValues: any;

	// Profile Image Properties
	profileImageFile: File | null = null;
	profileImagePreview: string | null = null;
	existingProfileImage: string | null = null;
	imageUploadError: string | null = null;
	imageUploadSuccess: string | null = null;
	employeeFirstName: string = '';
	employeeLastName: string = '';
	shouldRemoveImage: boolean = false;
	isImageUploading: boolean = false;

	softwareTypes: SoftwareTypeOption[] = [];
	showActivationMore = false;
	/** Quiet secondary control for Windows 7 & 8. */
	showOlderWindows = false;

	titleMsg: any = "Add New User"

	/** Password stays out of the way while credentials are emailed (a generated one is still sent). */
	get passwordFieldVisible(): boolean {
		const send = !!this.createUserForm?.get('send_email')?.value;
		const randomEmail = !!this.createUserForm?.get('useRandomEmail')?.value;
		return !send || randomEmail;
	}

	/** Enter in a text field submits once the form is valid (same guard as the primary button). */
	submitOnEnter(event: Event): void {
		const target = event.target as HTMLElement | null;
		if (!target || target.tagName !== 'INPUT') {
			return;
		}
		const type = (target as HTMLInputElement).type;
		if (!['text', 'email', 'password'].includes(type)) {
			return;
		}
		event.preventDefault();
		if (!this.createUserForm?.valid || this.isDisable) {
			return;
		}
		if (this.isEdit) {
			this.EditNewUser();
		} else {
			this.createNewUser();
		}
	}

	/** Why "Add employee" is disabled, in plain words; empty when the form is ready. */
	get createDisabledReason(): string {
		if (this.isEdit || !this.createUserForm) {
			return '';
		}
		const form = this.createUserForm;
		const missing: string[] = [];
		if (form.get('first_name')?.invalid) {
			missing.push('first name');
		}
		if (form.get('last_name')?.invalid) {
			missing.push('last name');
		}
		if (form.get('email')?.invalid) {
			missing.push(form.get('email')?.value ? 'a valid email' : 'email');
		}
		if (form.get('password')?.invalid) {
			missing.push('a valid password');
		}
		if (!missing.length) {
			return '';
		}
		const last = missing.pop() as string;
		return `Add ${missing.length ? missing.join(', ') + ' and ' + last : last} to continue`;
	}

	/** Label only (the dropdown panel also shows each role's description). */
	get selectedRoleLabel(): string {
		const value = String(this.createUserForm?.get('roles2')?.value || 'employee').toLowerCase();
		return this.creatableRoleOptions.find((o) => o.value === value)?.label || '';
	}

	/** One-line description for the currently selected access role. */
	get selectedRoleDescription(): string {
		const value = String(this.createUserForm?.get('roles2')?.value || 'employee').toLowerCase();
		const opt = this.creatableRoleOptions.find((o) => o.value === value);
		return opt?.description || '';
	}

	get isActivationHandoff(): boolean {
		return !!this.dialogData?.activationHandoff;
	}

	/** Journey words for the card already selected during activation handoff. */
	get activationHandoffLead(): string {
		const versionType = String(this.createUserForm?.get('version_type')?.value || '');
		const trayOff = this.activationJourney.getState()?.pilotTrayOff === true;
		if (versionType === 'restricted') {
			if (trayOff) {
				return 'You chose: only an admin can pause it. That is Admin Controlled. Creating them this way keeps the icon visible for this person.';
			}
			return 'You chose: only an admin can pause it. That is Admin Controlled.';
		}
		if (versionType === 'silent') {
			return 'You chose: no sign-in and no icon. That is Hidden.';
		}
		return 'You chose: the employee can pause it. That is Employee Controlled.';
	}

	constructor(
		@Inject(MAT_DIALOG_DATA) public dialogData: CreateUserDialogData,
		private loginService: LoginService,
		private router: Router,
		private _route: ActivatedRoute,
		private _modalService: ModalService,
		private _location: Location,
		private _sharedService: SharedService,
		private _projectService: ProjectService,
		private _snackBar: MatSnackBar,
		private dialog: MatDialog,
		private deviceDetectionService: DeviceDetectionService,
		private phoneVerificationService: PhoneVerificationService,
		private tokenEncryptionService: TokenEncryptionService,
		private entitlementsService: EntitlementsService,
		private activationJourney: ActivationJourneyService,
		private dialogRef: MatDialogRef<CreateUserComponent, CreateUserDialogResult>
	) { }

	private getSessionUser(): (UserRoleContext & { id?: number | string; _id?: number | string }) | null {
		return (
			this.tokenEncryptionService.getEncryptedItem<any>('logedInUser') ||
			this.tokenEncryptionService.getEncryptedItem<any>('user') ||
			null
		);
	}

	private hasManagerRolesEntitlement(): boolean {
		return this.entitlementsService.hasCompany(EntitlementKey.manager_roles);
	}

	refreshSoftwareTypeOptions(): void {
		this.softwareTypes = getSoftwareTypeOptions(
			this.entitlementsService.getPlanCode(),
			this.entitlementsService.isEnforcementActive(),
			this.entitlementsService.hasCompany(EntitlementKey.hidden_mode)
		);

		if (this.isEdit || !this.createUserForm) {
			return;
		}

		const currentType = this.createUserForm.get('version_type')?.value;
		const currentOption = this.softwareTypes.find((option) => option.value === currentType);
		if (!currentType || currentOption?.locked) {
			const defaultType = this.resolvePreferredVersionType();
			if (defaultType) {
				this.applyVersionTypeChange(defaultType);
			} else {
				this.createUserForm.get('version_type')?.setValue('');
			}
		}
	}

	/** Everyday default is Employee Controlled; handoff prefers the journey pause choice. */
	private resolvePreferredVersionType(): SoftwareVersionType | null {
		if (this.isActivationHandoff && !this.isEdit) {
			const handoffType = resolveActivationHandoffVersionType(this.activationJourney.getState());
			const handoffOption = this.softwareTypes.find((option) => option.value === handoffType);
			if (handoffOption && !handoffOption.locked) {
				return handoffType;
			}
		}
		return getDefaultSoftwareVersionType(this.softwareTypes);
	}

	onSoftwareTypeCardClick(softwareType: SoftwareTypeOption): void {
		if (softwareType.locked) {
			this.showSoftwareTypeUpgradeDialog(softwareType.value);
			return;
		}
		this.onVersionTypeChange(softwareType.value);
	}

	private showSoftwareTypeUpgradeDialog(versionType: string): void {
		const versionTypeError = validateSoftwareVersionType(
			versionType,
			this.entitlementsService.getPlanCode(),
			this.entitlementsService.isEnforcementActive(),
			this.entitlementsService.hasCompany(EntitlementKey.hidden_mode)
		);
		if (!versionTypeError) {
			return;
		}

		const ref = this.dialog.open(PlanUpgradeDialogComponent, {
			width: '420px',
			maxWidth: '94vw',
			data: {
				title: 'Upgrade required',
				message: versionTypeError,
			},
		});
		ref.afterClosed().subscribe((confirmed) => {
			if (confirmed) {
				void this.router.navigate(['/billings'], { queryParams: { intent: 'upgrade' } });
			}
		});
	}

	refreshRoleOptions(): void {
		this.loggedInUser = this.getSessionUser();
		const hasManagerRoles = this.hasManagerRolesEntitlement();
		this.creatableRoleOptions = getCreatableUserRoleOptions(this.loggedInUser, hasManagerRoles);
		this.roleSelectHint = getRoleSelectHint(this.loggedInUser, hasManagerRoles);
		this.showRoleField =
			!this.isEdit ||
			canAssignManagerUserRole(this.loggedInUser, hasManagerRoles) ||
			canAssignLeadUserRole(this.loggedInUser, hasManagerRoles);
		const currentRole = this.createUserForm?.get('roles2')?.value;
		if (
			currentRole &&
			!this.creatableRoleOptions.some((o) => o.value === String(currentRole).toLowerCase())
		) {
			this.createUserForm?.get('roles2')?.setValue('employee');
		}
	}

	private validateRoleBeforeSubmit(roles2: string): string | null {
		return validateRoleAssignmentForEditor(
			this.loggedInUser ?? undefined,
			roles2,
			this.hasManagerRolesEntitlement()
		);
	}

	createNewForm() {
		this.createUserForm = new UntypedFormGroup({
			first_name: new UntypedFormControl('', [Validators.required]),
			last_name: new UntypedFormControl('', [Validators.required]),
			email: new UntypedFormControl('', [Validators.required, Validators.pattern(this.emailPattern)]),
			password: new UntypedFormControl(this.generateRandomPassword(), [Validators.required, Validators.minLength(8), Validators.maxLength(20), passwordValidator()]),
			// confirmPassword: new UntypedFormControl('', [Validators.required]),
			send_email: new UntypedFormControl(true),
			roles2: new UntypedFormControl('employee', [Validators.required]),
			version_type: new UntypedFormControl('general', [Validators.required]),
			operating_system: new UntypedFormControl('windows', [Validators.required]),
			useRandomEmail: new UntypedFormControl(false)
		});

		this._route.params.subscribe((params) => {
			this.groupId.push(params.groupId);
		});


		if (this.isEdit == true) {
			this._projectService.getEmployeeData(this.gID).subscribe((res: any) => {
				let name = res.name.split(" ");
				this.userEmail = res.email;
				let first_name = "";
				let last_name = "";
				first_name = res.first_name;
				last_name = res.last_name;
				this.employeeFirstName = first_name;
				this.employeeLastName = last_name;
				this.createUserForm.controls['first_name'].setValue(first_name);
				this.createUserForm.controls['last_name'].setValue(last_name);
				// Load existing profile image if available
				console.log("res.meta ::: ", res.meta);
				if (res.meta && res.meta.profile_image_url) {
					this.existingProfileImage = res.meta.profile_image_url;
					this.profileImagePreview = res.meta.profile_image_url;
				} else {
					// No existing image, ensure initials will be shown
					this.existingProfileImage = null;
					this.profileImagePreview = null;
				}

				this.createUserForm = new UntypedFormGroup({
					first_name: new UntypedFormControl(first_name, [Validators.required]),
					last_name: new UntypedFormControl(last_name, [Validators.required]),
					roles2: new UntypedFormControl(res.role[0] == 'employee' && res.role.length > 1 ? res.role[1] : 'employee', [Validators.required]),
					operating_system: new UntypedFormControl(res.operating_system || 'windows', [Validators.required])
				});
				this.refreshRoleOptions();
			})
		}
	}

	editFormFill() {
		this._projectService.getEmployeeData(this.gID).subscribe((res: any) => {
			this.userEmail = res.email;
			let first_name = "";
			let last_name = "";
			first_name = res.first_name;
			last_name = res.last_name;
			this.employeeFirstName = first_name;
			this.employeeLastName = last_name;
			this.createUserForm.controls['first_name'].setValue(first_name);
			this.createUserForm.controls['last_name'].setValue(last_name);

			// Load existing profile image if available
			if (res.meta && res.meta.profile_image_url) {
				this.existingProfileImage = res.meta.profile_image_url;
				this.profileImagePreview = res.meta.profile_image_url;
			} else {
				// No existing image, ensure initials will be shown
				this.existingProfileImage = null;
				this.profileImagePreview = null;
			}

			this.createUserForm = new UntypedFormGroup({
				first_name: new UntypedFormControl(first_name, [Validators.required]),
				last_name: new UntypedFormControl(last_name, [Validators.required]),
				roles2: new UntypedFormControl(res.role[0] == 'employee' && res.role.length > 1 ? res.role[1] : 'employee', [Validators.required]),
				operating_system: new UntypedFormControl(res.operating_system || 'windows', [Validators.required])
			});
			this.refreshRoleOptions();
		})
	}

	ngOnInit() {
		if (this.dialogData && this.dialogData.isUserEditable) {
			this.isEdit = this.dialogData.isUserEditable;
			this.gID = this.dialogData.editUserId
		}

		this.entitlementsService.syncFromSession();
		this.createNewForm();
		const loggedInUser = this.getSessionUser();
		this.adminId = loggedInUser?._id || loggedInUser?.id || null;
		this.refreshRoleOptions();
		this.refreshSoftwareTypeOptions();
		this.applyActivationHandoffVersionType();
		this.applySendEmailDefault();
		if (this.createUserForm?.get('operating_system')?.value === 'windows_7_8') {
			this.showOlderWindows = true;
		}
		this.entitlementsSub = this.entitlementsService.stateChanged$.subscribe(() => {
			this.refreshRoleOptions();
			this.refreshSoftwareTypeOptions();
		});
	}

	/** When the journey opens create, preselect from pause choice (not tray-off → Hidden). */
	private applyActivationHandoffVersionType(): void {
		if (!this.isActivationHandoff || this.isEdit || !this.createUserForm) {
			return;
		}
		const preferred = this.resolvePreferredVersionType();
		if (!preferred) {
			return;
		}
		if (this.createUserForm.get('version_type')?.value === preferred) {
			return;
		}
		this.applyVersionTypeChange(preferred);
	}

	ngOnDestroy(): void {
		this.entitlementsSub?.unsubscribe();
	}

	onVersionTypeChange(versionType: string) {
		const versionTypeError = validateSoftwareVersionType(
			versionType,
			this.entitlementsService.getPlanCode(),
			this.entitlementsService.isEnforcementActive(),
			this.entitlementsService.hasCompany(EntitlementKey.hidden_mode)
		);
		if (versionTypeError) {
			this.showSoftwareTypeUpgradeDialog(versionType);
			return;
		}

		this.applyVersionTypeChange(versionType);
	}

	private applyVersionTypeChange(versionType: string) {
		console.log(this.createUserForm.value);
		const { first_name, last_name, operating_system } = this.createUserForm.value;

		if (versionType === 'silent') {
			if (this.createUserForm.get('version_type')?.value === 'general') {
				this.generalFormValues = _.cloneDeep(this.createUserForm.value);
			} else if (this.createUserForm.get('version_type')?.value === 'restricted') {
				this.restrictedFormValues = _.cloneDeep(this.createUserForm.value);
			}

			this.createUserForm.reset({ version_type: versionType, roles2: 'employee' });

			this.createUserForm.patchValue({
				first_name,
				last_name,
				operating_system,
				email: this.generateRandomEmail(),
				password: this.generateRandomPassword(),
				useRandomEmail: true,
				send_email: false
			});

		}
		else if (versionType === 'general') {
			if (this.createUserForm.get('version_type')?.value === 'silent') {
				this.silentFormValues = _.cloneDeep(this.createUserForm.value);
			} else if (this.createUserForm.get('version_type')?.value === 'restricted') {
				this.restrictedFormValues = _.cloneDeep(this.createUserForm.value);
			}

			this.createUserForm.reset({ version_type: versionType, roles2: 'employee' });

			this.createUserForm.patchValue({
				first_name,
				last_name,
				operating_system,
				email: this.generalFormValues?.email || this.restrictedFormValues?.email || '',
				password: this.generalFormValues?.password || this.generateRandomPassword(),
				useRandomEmail: this.generalFormValues?.useRandomEmail || false,
				send_email: this.generalFormValues?.send_email ?? true
			});
			this.applySendEmailDefault();
		}
		else if (versionType === 'restricted') {
			if (this.createUserForm.get('version_type')?.value === 'general') {
				this.generalFormValues = _.cloneDeep(this.createUserForm.value);
			} else if (this.createUserForm.get('version_type')?.value === 'silent') {
				this.silentFormValues = _.cloneDeep(this.createUserForm.value);
			}

			this.createUserForm.reset({ version_type: versionType, roles2: 'employee' });

			this.createUserForm.patchValue({
				first_name,
				last_name,
				operating_system,
				email: this.generalFormValues?.email || this.restrictedFormValues?.email || '',
				password: this.generalFormValues?.password || this.restrictedFormValues?.password || this.generateRandomPassword(),
				useRandomEmail: this.generalFormValues?.useRandomEmail || this.restrictedFormValues?.useRandomEmail || false,
				send_email: this.generalFormValues?.send_email ?? this.restrictedFormValues?.send_email ?? true
			});
			this.applySendEmailDefault();
		}

	}

	/** Manual/Automatic: email credentials by default unless random email is on. */
	private applySendEmailDefault(): void {
		if (this.isEdit || !this.createUserForm) {
			return;
		}
		const vt = this.createUserForm.get('version_type')?.value;
		if (vt === 'silent' || this.createUserForm.get('useRandomEmail')?.value) {
			this.createUserForm.get('send_email')?.setValue(false);
			return;
		}
		this.createUserForm.get('send_email')?.setValue(true);
	}

	/** @deprecated Use applySendEmailDefault — kept for activation chrome specs. */
	private applyActivationSendEmailDefault(): void {
		this.applySendEmailDefault();
	}

	onOperatingSystemChange(os: string) {
		this.createUserForm.patchValue({ operating_system: os });
		if (os === 'windows_7_8') {
			this.showOlderWindows = true;
		}
	}

	onUseRandomEmailChange(event: any) {
		if (event.checked) {
			this.createUserForm.patchValue({ email: this.generateRandomEmail() });
			this.createUserForm.get('send_email')?.setValue(false);
		} else {
			this.createUserForm.patchValue({ email: "" });
		}
	}

	createNewUser() {
		this.isDisable = true;
		if (!this.createUserForm.valid) {
			this.isDisable = false;
			return;
		}

		const roleError = this.validateRoleBeforeSubmit(this.createUserForm.get('roles2')?.value);
		if (roleError) {
			this.isDisable = false;
			this._snackBar.open(roleError, 'Close', { duration: 5000 });
			return;
		}

		const versionTypeError = validateSoftwareVersionType(
			this.createUserForm.get('version_type')?.value,
			this.entitlementsService.getPlanCode(),
			this.entitlementsService.isEnforcementActive(),
			this.entitlementsService.hasCompany(EntitlementKey.hidden_mode)
		);
		if (versionTypeError) {
			this.isDisable = false;
			this._snackBar.open(versionTypeError, 'Close', { duration: 5000 });
			return;
		}

		if (this.createUserForm.get('version_type')?.value === 'silent') {
			this.createUserForm.patchValue({
				send_email: false
			});
		}

		const formValue = this.createUserForm.value;
		console.log('[CreateUser] Submit clicked with version_type=', formValue?.version_type);

		const performCreate = () => {
			formValue.username = formValue.email;
			this.loginService.addUser(formValue).subscribe((newEmployeeData: any) => {
				this.isDisable = false;

				const closeWithCreateSuccess = () => {
					const employee: CreatedEmployeePayload = {
						employeeId: Number(newEmployeeData.id),
						name: newEmployeeData.name || `${formValue.first_name || ''} ${formValue.last_name || ''}`.trim(),
						email: formValue.email,
						versionType: formValue.version_type,
						operatingSystem: formValue.operating_system,
						sendEmail: !!formValue.send_email,
						firstName: formValue.first_name || '',
						lastName: formValue.last_name || '',
						installerPassword: formValue.password || undefined,
					};
					this.isResponseGet = true;
					this.dialogRef.close({ reason: 'create_success', employee });
				};

				// For Admin Control / Hidden, ensure settings are saved before closing
				if (formValue.version_type === 'restricted') {
					this._projectService.createAdminControlUserSettings(newEmployeeData.id).subscribe(
						() => {
							closeWithCreateSuccess();
						},
						(error) => {
							console.error('Failed to create admin control user settings:', error);
							this._snackBar.open('User created, but failed to apply company settings. Please try again from Settings.', 'Close', { duration: 3000 });
							closeWithCreateSuccess();
						}
					);
				} else if (formValue.version_type === 'silent') {
					this._projectService.createSilentUserSettings(newEmployeeData.id).subscribe(
						() => {
							closeWithCreateSuccess();
						},
						(error) => {
							console.error('Failed to create silent user settings:', error);
							this._snackBar.open('User created, but failed to enable Silent Mode. Please turn it on from Settings.', 'Close', { duration: 3000 });
							closeWithCreateSuccess();
						}
					);
				} else {
					closeWithCreateSuccess();
				}
			}, error => {
				this.isDisable = false;
				const errMsg = (error && error.error && error.error.message) ? error.error.message : 'Failed to create user';
				this._snackBar.open(errMsg, 'Close', { duration: 3000 });
				this.isResponseGet = true;
				// Ensure next attempt starts fresh to avoid reusing the same random email
				this.resetFormState();
			});
		}

		// Show confirmation when Admin Control is selected since it applies company settings
		if ((formValue.version_type || '').trim() === 'restricted') {
			console.log('[CreateUser] Showing Admin Control confirmation');
			// Skip confirmation if user opted out previously
			const skipKey = 'skipAdminControlConfirm';
			if (localStorage.getItem(skipKey) === 'true') {
				performCreate();
				return;
			}

			const dialogRef = this.dialog.open(AdminControlConfirmDialogComponent, {
				width: '440px',
				disableClose: true,
				panelClass: 'centered-dialog',
				data: {
					title: 'Create them as Admin Controlled?',
					subtitle: 'They will sign in. They cannot pause tracking.',
					bullets: [
						'They sign in and cannot pause tracking',
						'Your company settings are applied to them',
						'The icon stays visible on their computer',
						'You can change their settings later',
					],
					confirmText: 'Create as Admin Controlled',
					cancelText: 'Cancel',
				},
			});
			dialogRef.afterClosed().subscribe((result: any) => {
				if (result && result.dontShowAgain) {
					localStorage.setItem(skipKey, 'true');
				}
				if (result && result.confirmed) {
					performCreate();
				} else {
					this.isDisable = false;
				}
			});
		} else {
			console.log('[CreateUser] Non Admin Control flow; proceeding without confirmation');
			performCreate();
		}
	}

	capitalizeOS(os: string): string {
		if (!os) return '';
		return os.charAt(0).toUpperCase() + os.slice(1).toLowerCase();
	}

	createUser() {
		this.loading = true;
		this.isResponseGet = false;
		if (!this.createUserForm.valid) {
			return;
		}
		Object.assign(this.createUserForm.value, { admin: this.adminId });
		this.createUserForm.value.username = this.createUserForm.value.email;
		this.loginService.addUser(this.createUserForm.value).subscribe(checkAddUser => {
			this.loading = false;
			swal.fire({
				position: 'center',
				icon: 'success',
				title: 'Employee created',
				showConfirmButton: false,
				timer: 3000,
			})
			this.isResponseGet = true;
			if (this.groupId && this.groupId[0] != 'all') {
				this.router.navigate(['/employees', this.groupId[0]]);

			}
			else {
				this.router.navigate(['/projects']);

			}
			this.createUserForm.reset();

		}, (error) => {
			this.loading = false;
			const apiReason = String(error?.error?.message || '').trim();
			const technical = /exception|stack|sql|undefined|status code|econn|token|null/i.test(apiReason);
			swal.fire({
				icon: 'error',
				title: "Couldn't create the employee",
				text: !apiReason || technical
					? 'Check the details and try again. If this keeps happening, contact support.'
					: (apiReason.length > 160 ? `${apiReason.slice(0, 157)}...` : apiReason),
				confirmButtonText: 'Try again',
			})
			this.isResponseGet = true;
			this.isResponseGet = true;
		});

	}

	changeAnotherRole(value) {
		const currentRole = this.createUserForm.get('roles2').value;
		if (currentRole === this.selectedValue) {
			this.createUserForm.get('roles2').patchValue('')
		} else {
			this.createUserForm.get('roles2').patchValue(value)
		}
		this.selectedValue = value;
	}

	/**
	 * Saves profile image separately using upload API
	 * This is independent from form data saving
	 */
	saveProfileImage(): void {
		if (!this.gID) {
			this.imageUploadError = 'User ID is required';
			return;
		}

		this.isImageUploading = true;
		this.imageUploadError = null;
		this.imageUploadSuccess = null;

		// Check if opened from profile screen
		const fromProfileScreen = this.dialogData && this.dialogData.fromProfileScreen;

		// Handle image removal - only removes profile image URL, no other data is updated
		if (this.shouldRemoveImage) {
			this._projectService.removeProfileImage(this.gID).subscribe(
				(response) => {
					this.isImageUploading = false;
					this.imageUploadSuccess = 'Profile image removed successfully';
					this.shouldRemoveImage = false;
					this.existingProfileImage = null;
					this.profileImagePreview = null;
					this.profileImageFile = null;
					this._snackBar.open('Profile image removed successfully', 'Close', {
						duration: 3000
					});
					// Refresh user data to get updated image
					this._projectService.updateEmployeeList({});

					// If opened from profile screen, close modal to refresh profile data
					if (fromProfileScreen) {
						setTimeout(() => {
							this.dialogRef.close({ reason: 'edit_success', updated: true });
						}, 1500); // Wait for success message to show
					}
				},
				(error) => {
					this.isImageUploading = false;
					this.imageUploadError = 'Failed to remove profile image. Please try again.';
					this._snackBar.open('Failed to remove profile image', 'Close', {
						duration: 3000
					});
				}
			);
			return;
		}

		// Handle image upload
		if (this.profileImageFile) {
			this._projectService.uploadProfileImage(this.gID, this.profileImageFile).subscribe(
				(uploadResponse: any) => {
					this.isImageUploading = false;
					this.imageUploadSuccess = 'Profile image uploaded successfully';
					this.profileImageFile = null;
					// Update existing image preview if response contains image URL
					if (uploadResponse?.body?.profile_image) {
						this.existingProfileImage = uploadResponse.body.profile_image;
						this.profileImagePreview = null;
					}
					this._snackBar.open('Profile image uploaded successfully', 'Close', {
						duration: 3000
					});
					// Refresh user data to get updated image
					this._projectService.updateEmployeeList({});

					// If opened from profile screen, close modal to refresh profile data
					if (fromProfileScreen) {
						setTimeout(() => {
							this.dialogRef.close({ reason: 'edit_success', updated: true });
						}, 1500); // Wait for success message to show
					}
				},
				(uploadError) => {
					this.isImageUploading = false;
					this.imageUploadError = 'Failed to upload profile image. Please try again.';
					this._snackBar.open('Failed to upload profile image', 'Close', {
						duration: 3000
					});
				}
			);
		} else {
			this.isImageUploading = false;
			this.imageUploadError = 'Please select an image first';
		}
	}

	/**
	 * Saves form data only (name, roles, etc.)
	 * This does NOT handle image uploads - images must be saved separately using saveProfileImage()
	 */
	EditNewUser() {
		this.isResponseGet = false;
		if (!this.createUserForm.valid) {
			return;
		}

		const roleError = this.validateRoleBeforeSubmit(this.createUserForm.get('roles2')?.value);
		if (roleError) {
			this._snackBar.open(roleError, 'Close', { duration: 5000 });
			return;
		}

		// Prepare form data (DO NOT include image file - images are uploaded separately)
		const formData = { ...this.createUserForm.value };

		// Note: Image removal flag is NOT included here - it's handled by saveProfileImage()
		// Form data saving is completely independent from image operations

		// Save form data using separate API (form saving endpoint)
		this.updateEmployeeData(formData);
	}

	updateEmployeeData(formData: any): void {
		const isDowngradingToEmployee = formData.roles2 === 'employee';
		const employeeUpdate$ = this._projectService.editEmployee(this.gID, formData);
		
		let updateObservable$ = employeeUpdate$;
		
		if (isDowngradingToEmployee) {
			updateObservable$ = employeeUpdate$.pipe(
				switchMap((employeeRes) => {
					return this._projectService.getTeamList().pipe(
						switchMap((teams: any) => {
							const teamUpdates: any[] = [];
							const empIdStr = String(this.gID);
							const teamsArray = teams || [];
							
							for (const team of teamsArray) {
								if (!team || !team.meta) continue;
								let modified = false;
								
								const managers = (team.meta.team_manager || '').split('~').filter(Boolean);
								const remainingManagers = managers.filter((m: string) => m !== empIdStr);
								if (managers.length !== remainingManagers.length) modified = true;
								const formatedManager = remainingManagers.length ? '~' + remainingManagers.join('~') + '~' : '';
								
								const leads = (team.meta.team_lead || '').split('~').filter(Boolean);
								const remainingLeads = leads.filter((l: string) => l !== empIdStr);
								if (leads.length !== remainingLeads.length) modified = true;
								const formatedLead = remainingLeads.length ? '~' + remainingLeads.join('~') + '~' : '';
								
								if (modified) {
									const teamData = {
										title: team.title?.rendered || team.title,
										slug: team.slug,
										meta: {
											team_manager: formatedManager,
											team_lead: formatedLead,
											team_developer: team.meta.team_developer || ''
										}
									};
									teamUpdates.push(this._projectService.editTeamMeamber(team.id, teamData));
								}
							}
							
							if (teamUpdates.length > 0) {
								return forkJoin(teamUpdates).pipe(map(() => employeeRes));
							}
							return of(employeeRes);
						}),
						catchError((err) => {
							console.error('Failed to update teams on role downgrade', err);
							return of(employeeRes);
						})
					);
				})
			);
		}
		
		updateObservable$.subscribe(data => {
			this.isResponseGet = true;
			this.imageUploadSuccess = null;
			this.imageUploadError = null;
			this.shouldRemoveImage = false;

			// Check if opened from profile screen
			const fromProfileScreen = this.dialogData && this.dialogData.fromProfileScreen;

			swal.fire({
				position: 'center',
				icon: 'success',
				title: 'Employee Updated Successfully',
				showConfirmButton: false,
				timer: 1500,
			}).then(() => {
				this._sharedService.isEdit = false;
				this._projectService.updateEmployeeList({})
				// this._projectService.updateEmployeeList({})

				if (fromProfileScreen) {
					// Close modal and return success flag to refresh data in current route
					this.dialogRef.close({ reason: 'edit_success', updated: true });
				} else if (this.dialogData && this.dialogData.isRedirect === false) {
					// Don't redirect, just close the modal
					this.dialogRef.close({ reason: 'edit_success', updated: true });
				} else {
					// Navigate to employees list (original behavior)
					this.router.navigate(["/employees"]);
					this.modalClose();
				}
			});
		}, err => {
			this.isResponseGet = true;
			this._snackBar.open('Something went wrong', 'Undo', {
				duration: 3000
			})
		});
	}

	blankValueInInputs() {
		this.createUserForm.reset();
	}

	modalClose() {
		this.dialogRef.close({ reason: 'cancel' });
	}

	copyPassword(): void {
		const password = this.createUserForm.get('password')?.value;
		if (password) {
			navigator.clipboard.writeText(password).then(() => {
				this._snackBar.open('Password copied to clipboard', 'Close', { duration: 3000 });
			});
		}
	}

	generateRandomPassword(): string {
		const minLength = 8;
		const maxLength = 12;
		// Exclude look-alikes: I/l/1, O/0
		const specialChars = ['@', '!', '$', '%', '^', '&', '*', '(', ')', '_', '+', '=', '-', '|', ':', '<', '>', '?', '{', '}', '[', ']'];
		const uppercaseChars = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
		const lowercaseChars = 'abcdefghijkmnopqrstuvwxyz';
		const numbers = '23456789';
		const allChars = uppercaseChars + lowercaseChars + numbers;

		const length = Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength;

		let password = '';
		password += uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)];
		password += lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)];
		password += numbers[Math.floor(Math.random() * numbers.length)];

		const specialChar = specialChars[Math.floor(Math.random() * specialChars.length)];

		const remainingLength = length - 4;

		for (let i = 0; i < remainingLength; i++) {
			password += allChars[Math.floor(Math.random() * allChars.length)];
		}

		const middleIndex = Math.floor(password.length / 2);
		password = password.slice(0, middleIndex) + specialChar + password.slice(middleIndex);

		return password;
	}

	generateAndSetRandomPassword(): void {
		const randomPassword = this.generateRandomPassword();
		this.createUserForm.get('password')?.setValue(randomPassword);
	}

	generateRandomEmail(): string {
		const randomString = Math.random().toString(36).substring(2, 12);
		const domain = this.fakeEmailDomains[Math.floor(Math.random() * this.fakeEmailDomains.length)];
		return `${randomString}@${domain}`;
	}
	copyEmail(): void {
		const email = this.createUserForm.get('email')?.value;
		if (email) {
			navigator.clipboard.writeText(email).then(() => {
				this._snackBar.open('Email copied to clipboard', 'Close', { duration: 3000 });
			});
		}
	}
	generateAndSetRandomEmail(): void {
		const randomEmail = this.generateRandomEmail();
		this.createUserForm.get('email')?.setValue(randomEmail);
	}

	resetFormState(): void {
		this.generalFormValues = undefined;
		this.silentFormValues = undefined;
		this.restrictedFormValues = undefined;
		this.isRandomEmail = false;
		this.selectedValue = '';
		this.userEmail = '';
		if (this.createUserForm) {
			this.createUserForm.reset({
				first_name: '',
				last_name: '',
				email: '',
				password: this.generateRandomPassword(),
				send_email: false,
				roles2: 'employee',
				version_type: 'general',
				operating_system: 'windows',
				useRandomEmail: false
			});
		}
	}

	// Profile Image Methods
	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length > 0) {
			const file = input.files[0];
			this.validateAndProcessImage(file);
		}
	}

	validateAndProcessImage(file: File): void {
		this.imageUploadError = null;
		this.imageUploadSuccess = null;

		// Validate file type
		const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
		const validExtensions = ['.jpeg', '.jpg', '.png', '.webp'];
		const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

		if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
			this.imageUploadError = 'Invalid file type. Please select JPEG, JPG, PNG, or WEBP image.';
			return;
		}

		// Validate file size (max 2MB)
		const maxSize = 2 * 1024 * 1024; // 2MB
		if (file.size > maxSize) {
			this.imageUploadError = 'File size exceeds 2MB limit. Please select a smaller image.';
			return;
		}

		// Reset removal flag if new image is selected
		this.shouldRemoveImage = false;

		// Show preview
		const reader = new FileReader();
		reader.onload = (e: any) => {
			this.profileImagePreview = e.target.result;
		};
		reader.readAsDataURL(file);

		// Convert to webp if not already webp
		if (file.type !== 'image/webp') {
			this.convertToWebP(file).then((webpFile) => {
				// Validate converted file size
				if (webpFile.size > maxSize) {
					this.imageUploadError = 'Converted image size exceeds 2MB limit. Please select a smaller image.';
					this.profileImagePreview = null;
					this.profileImageFile = null;
					return;
				}
				this.profileImageFile = webpFile;
				this.imageUploadSuccess = 'Image ready. Click "Save Image" button to upload.';
			}).catch((error) => {
				this.imageUploadError = 'Failed to process image. Please try again.';
				console.error('Image conversion error:', error);
			});
		} else {
			this.profileImageFile = file;
			this.imageUploadSuccess = 'Image ready. Click "Save Image" button to upload.';
		}
	}

	convertToWebP(file: File): Promise<File> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (e: any) => {
				const img = new Image();
				img.onload = () => {
					const canvas = document.createElement('canvas');
					canvas.width = img.width;
					canvas.height = img.height;
					const ctx = canvas.getContext('2d');
					if (!ctx) {
						reject(new Error('Could not get canvas context'));
						return;
					}
					ctx.drawImage(img, 0, 0);
					canvas.toBlob((blob) => {
						if (blob) {
							const webpFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.webp'), {
								type: 'image/webp',
								lastModified: Date.now()
							});
							resolve(webpFile);
						} else {
							reject(new Error('Failed to convert image to webp'));
						}
					}, 'image/webp', 0.9); // 90% quality
				};
				img.onerror = () => reject(new Error('Failed to load image'));
				img.src = e.target.result;
			};
			reader.onerror = () => reject(new Error('Failed to read file'));
			reader.readAsDataURL(file);
		});
	}

	removeProfileImage(): void {
		this.profileImageFile = null;
		this.profileImagePreview = null;
		this.shouldRemoveImage = true; // Flag to remove image on save
		this.imageUploadError = null;
		this.imageUploadSuccess = null;
	}

	getInitials(): string {
		const firstName = this.employeeFirstName || this.createUserForm?.get('first_name')?.value || '';
		const lastName = this.employeeLastName || this.createUserForm?.get('last_name')?.value || '';
		const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
		const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
		return (firstInitial + lastInitial) || 'U';
	}
}
